import { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    Alert,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useMediaQuery,
    useTheme,
    Snackbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import { PackingOperation, PackingTask } from '../schema';
import { groupTasksByDestination, type PackingListItemLike } from '../utils/taskDisplayHelpers';
import { batchUpdateTaskCompletions, type ItemLocationForLookup } from '../utils/taskCompletion';
import TaskRow from './TaskRow';
import AmbiguousTasksSection from './AmbiguousTasksSection';

interface Props {
    operation: PackingOperation;
    tasks: PackingTask[];
    allItemLocations: ItemLocationForLookup[];
    onToggleInventoryPanel: () => void;
}

const TOUCH_TARGET_MIN_HEIGHT = 48;

export default function PackingTasksList({ operation, tasks, allItemLocations, onToggleInventoryPanel }: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showFilter, setShowFilter] = useState<'all' | 'completed' | 'incomplete' | 'warnings'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState<'default' | 'name-asc' | 'name-desc' | 'qty-high' | 'qty-low' | 'status'>('default');
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [bulkError, setBulkError] = useState<string | null>(null);

    const isReadOnly = operation.status === 'completed';
    const incompleteTasks = useMemo(() => tasks.filter(t => !t.completed), [tasks]);
    const completedTasks = useMemo(() => tasks.filter(t => t.completed), [tasks]);

    const handleCheckAll = async () => {
        if (isReadOnly || incompleteTasks.length === 0 || bulkUpdating) return;
        setBulkUpdating(true);
        setBulkError(null);
        try {
            await batchUpdateTaskCompletions(incompleteTasks, true, allItemLocations);
        } catch (err: any) {
            setBulkError(err.message || 'Failed to check all.');
        } finally {
            setBulkUpdating(false);
        }
    };

    const handleUncheckAll = async () => {
        if (isReadOnly || completedTasks.length === 0 || bulkUpdating) return;
        setBulkUpdating(true);
        setBulkError(null);
        try {
            await batchUpdateTaskCompletions(completedTasks, false, allItemLocations);
        } catch (err: any) {
            setBulkError(err.message || 'Failed to uncheck all.');
        } finally {
            setBulkUpdating(false);
        }
    };

    // Build packing list map from target events (for trailer inference per destination)
    const packingLists = useMemo(() => {
        const map = new Map<string, PackingListItemLike[]>();
        const targets = operation.targetEvents || [];
        for (const e of targets) {
            const items = (e as { id: string; packingListItems?: PackingListItemLike[] }).packingListItems;
            map.set(e.id, items || []);
        }
        return map;
    }, [operation.targetEvents]);

    // Split into ambiguous (verify) vs non-ambiguous for destination grouping
    const verifyTasks = useMemo(() => tasks.filter(t => t.ambiguous), [tasks]);

    // Filter and sort tasks
    const filteredAndSortedTasks = useMemo(() => {
        let filtered = tasks;

        // Apply show filter
        if (showFilter === 'completed') {
            filtered = filtered.filter(t => t.completed);
        } else if (showFilter === 'incomplete') {
            filtered = filtered.filter(t => !t.completed);
        }
        // 'warnings' filter will be implemented when warnings are added

        // Apply search
        if (searchTerm) {
            filtered = filtered.filter(t => {
                const itemName = t.itemType?.name || '';
                return itemName.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }

        // Apply sort
        if (sortOption === 'name-asc') {
            filtered = [...filtered].sort((a, b) => {
                const nameA = a.itemType?.name || '';
                const nameB = b.itemType?.name || '';
                return nameA.localeCompare(nameB);
            });
        } else if (sortOption === 'name-desc') {
            filtered = [...filtered].sort((a, b) => {
                const nameA = a.itemType?.name || '';
                const nameB = b.itemType?.name || '';
                return nameB.localeCompare(nameA);
            });
        } else if (sortOption === 'qty-high') {
            filtered = [...filtered].sort((a, b) => b.quantity - a.quantity);
        } else if (sortOption === 'qty-low') {
            filtered = [...filtered].sort((a, b) => a.quantity - b.quantity);
        } else if (sortOption === 'status') {
            filtered = [...filtered].sort((a, b) => {
                if (a.completed === b.completed) return 0;
                return a.completed ? 1 : -1;
            });
        }
        // 'default' keeps original order (grouped)

        return filtered;
    }, [tasks, showFilter, searchTerm, sortOption]);

    // Split filtered list into ambiguous and non-ambiguous, then group by destination
    const { destinationGroups, filteredVerify } = useMemo(() => {
        const verify: PackingTask[] = [];
        const nonAmbiguous: PackingTask[] = [];
        for (const task of filteredAndSortedTasks) {
            if (task.ambiguous) {
                verify.push(task);
            } else {
                nonAmbiguous.push(task);
            }
        }
        const groups = groupTasksByDestination(nonAmbiguous, operation, packingLists);
        const entries = Array.from(groups.entries()).sort((a, b) =>
            a[1].eventName.localeCompare(b[1].eventName, undefined, { sensitivity: 'base' })
        );
        return { destinationGroups: entries, filteredVerify: verify };
    }, [filteredAndSortedTasks, operation, packingLists]);

    const getSectionProgress = (sectionTasks: PackingTask[]) => {
        const completed = sectionTasks.filter(t => t.completed).length;
        const total = sectionTasks.length;
        return { completed, total };
    };

    /** Group tasks by item category (nested under destination). Categories and tasks within each sorted alphabetically by name. */
    const groupTasksByCategory = (sectionTasks: PackingTask[]): Array<{ categoryName: string; tasks: PackingTask[] }> => {
        const byCategory = new Map<string, PackingTask[]>();
        for (const task of sectionTasks) {
            const categoryName = task.itemType?.category?.name ?? 'Uncategorized';
            const list = byCategory.get(categoryName) ?? [];
            list.push(task);
            byCategory.set(categoryName, list);
        }
        return Array.from(byCategory.entries())
            .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map(([categoryName, tasks]) => ({
                categoryName,
                tasks: [...tasks].sort((a, b) =>
                    (a.itemType?.name ?? '').localeCompare(b.itemType?.name ?? '', undefined, { sensitivity: 'base' })
                ),
            }));
    };

    const verifyProgress = getSectionProgress(verifyTasks);

    return (
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            {/* Filter and Search Controls */}
            <Box
                sx={{
                    mb: 2,
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    '& .MuiButton-root': isMobile ? { minHeight: TOUCH_TARGET_MIN_HEIGHT, width: '100%' } : undefined,
                    '& .MuiTextField-root': isMobile ? { width: '100%' } : undefined,
                    '& .MuiFormControl-root': isMobile ? { width: '100%', minWidth: 0 } : undefined,
                }}
            >
                <Button
                    variant="outlined"
                    startIcon={<InventoryIcon />}
                    onClick={onToggleInventoryPanel}
                    size={isMobile ? 'medium' : 'small'}
                    sx={isMobile ? { minHeight: TOUCH_TARGET_MIN_HEIGHT } : undefined}
                >
                    Show Inventory
                </Button>

                <Button
                    variant="outlined"
                    size={isMobile ? 'medium' : 'small'}
                    startIcon={<DoneAllIcon />}
                    onClick={handleCheckAll}
                    disabled={isReadOnly || bulkUpdating || incompleteTasks.length === 0}
                    sx={isMobile ? { minHeight: TOUCH_TARGET_MIN_HEIGHT } : undefined}
                >
                    Check all
                </Button>
                <Button
                    variant="outlined"
                    size={isMobile ? 'medium' : 'small'}
                    startIcon={<RemoveDoneIcon />}
                    onClick={handleUncheckAll}
                    disabled={isReadOnly || bulkUpdating || completedTasks.length === 0}
                    sx={isMobile ? { minHeight: TOUCH_TARGET_MIN_HEIGHT } : undefined}
                >
                    Uncheck all
                </Button>

                {/* Search */}
                <TextField
                    size={isMobile ? 'medium' : 'small'}
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{ flexGrow: 1, minWidth: isMobile ? 0 : 200 }}
                />

                {/* Show Filter */}
                <FormControl size={isMobile ? 'medium' : 'small'} sx={{ minWidth: isMobile ? 0 : 150 }}>
                    <InputLabel>Show</InputLabel>
                    <Select value={showFilter} label="Show" onChange={e => setShowFilter(e.target.value as typeof showFilter)}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="completed">Completed Only</MenuItem>
                        <MenuItem value="incomplete">Incomplete Only</MenuItem>
                        <MenuItem value="warnings">With Warnings</MenuItem>
                    </Select>
                </FormControl>

                {/* Sort */}
                <FormControl size={isMobile ? 'medium' : 'small'} sx={{ minWidth: isMobile ? 0 : 150 }}>
                    <InputLabel>Sort</InputLabel>
                    <Select value={sortOption} label="Sort" onChange={e => setSortOption(e.target.value as typeof sortOption)}>
                        <MenuItem value="default">Default (by group)</MenuItem>
                        <MenuItem value="name-asc">By Item Name (A-Z)</MenuItem>
                        <MenuItem value="name-desc">By Item Name (Z-A)</MenuItem>
                        <MenuItem value="qty-high">By Quantity (High to Low)</MenuItem>
                        <MenuItem value="qty-low">By Quantity (Low to High)</MenuItem>
                        <MenuItem value="status">By Status (Incomplete first)</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Result Count */}
            {filteredAndSortedTasks.length !== tasks.length && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Showing {filteredAndSortedTasks.length} of {tasks.length} tasks
                </Typography>
            )}

            {/* Destination-based task groups */}
            {destinationGroups.map(([destId, group]) => {
                const progress = getSectionProgress(group.tasks);
                const trailerLabel = group.trailers.length > 0 ? ` (via ${group.trailers.map(t => t.name || 'Trailer').join(', ')})` : '';
                return (
                    <Accordion key={destId} defaultExpanded sx={{ mb: 2 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={
                                isMobile
                                    ? {
                                          minHeight: TOUCH_TARGET_MIN_HEIGHT,
                                          '& .MuiAccordionSummary-expandIconWrapper': { minWidth: 44, minHeight: 44 },
                                      }
                                    : undefined
                            }
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, flexWrap: 'wrap' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    To {group.eventName}
                                    {trailerLabel}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    [{progress.completed}/{progress.total}]
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: { xs: 1, sm: 3 } }}>
                            {groupTasksByCategory(group.tasks).map(({ categoryName, tasks }) => (
                                <Box key={categoryName} sx={{ mb: 2 }}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        sx={{ px: { xs: 1, sm: 2 }, py: 0.5, fontWeight: 600 }}
                                    >
                                        {categoryName}
                                    </Typography>
                                    <List disablePadding>
                                        {tasks.map(task => (
                                            <TaskRow
                                                key={task.id}
                                                task={task}
                                                operation={operation}
                                                packingLists={packingLists}
                                                allItemLocations={allItemLocations}
                                            />
                                        ))}
                                    </List>
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Verify Items Section (Ambiguous Tasks) */}
            {verifyTasks.length > 0 && (
                <AmbiguousTasksSection
                    operation={operation}
                    tasks={filteredVerify}
                    completedCount={verifyProgress.completed}
                    totalCount={verifyProgress.total}
                    packingLists={packingLists}
                    allItemLocations={allItemLocations}
                />
            )}

            {/* Empty State */}
            {tasks.length === 0 && <Alert severity="info">No tasks for this operation</Alert>}

            <Snackbar
                open={!!bulkError}
                autoHideDuration={6000}
                onClose={() => setBulkError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setBulkError(null)} severity="error" sx={{ width: '100%' }}>
                    {bulkError}
                </Alert>
            </Snackbar>
        </Box>
    );
}
